import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Room } from '../types'
import type { SendFn } from '../App'
import Instructions from './Instructions'
import ConfirmDialog from './ConfirmDialog'
import { GROUP_MODE_ENABLED } from '../../shared/messages'

interface MenuProps {
  room: Room
  playerId: string
  send: SendFn
  onChangeName: (name: string) => void
}

// "instructions" isn't in here because it opens a modal rather than
// expanding inline like the others.
type Section = 'profile' | 'leaderboard' | 'admin' | null

export default function Menu({ room, playerId, send, onChangeName }: MenuProps) {
  const [open, setOpen] = useState(false)
  const [section, setSection] = useState<Section>(null)
  const [instructionsOpen, setInstructionsOpen] = useState(false)

  const isHost = playerId === room.hostId
  const hostName = room.players[room.hostId]?.name || 'Host'
  const myRequestPending = room.hostRequest === playerId
  const someoneElseRequested = !!room.hostRequest && room.hostRequest !== playerId
  const requesterName = someoneElseRequested ? (room.players[room.hostRequest]?.name || '???') : ''
  const myName = room.players[playerId]?.name || ''

  const toggle = (next: Section) => setSection(section === next ? null : next)

  const requestHost = () => send('request_host', {})
  const cancelRequest = () => send('cancel_host_request', {})

  return (
    <>
      <button
        aria-label="Open menu"
        onClick={() => setOpen(true)}
        className="fixed top-3 right-3 z-40 w-12 h-12 rounded-xl bg-white hover:bg-cream border-2 border-warmblack/15 shadow-md flex flex-col items-center justify-center gap-[6px] active:scale-95 transition-all"
      >
        <span className="block w-6 h-[3px] bg-warmblack rounded" />
        <span className="block w-6 h-[3px] bg-warmblack rounded" />
        <span className="block w-6 h-[3px] bg-warmblack rounded" />
      </button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 bg-warmblack/30 z-40"
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 280 }}
              className="fixed top-0 right-0 bottom-0 z-50 w-full max-w-sm bg-cream shadow-2xl flex flex-col"
            >
              <div className="px-5 py-4 border-b border-warmblack/10">
                <div className="flex items-center justify-between">
                  <h2 className="font-display text-2xl font-bold text-warmblack">Menu</h2>
                  <button
                    aria-label="Close menu"
                    onClick={() => setOpen(false)}
                    className="w-9 h-9 rounded-lg hover:bg-warmblack/5 text-warmblack/60 text-2xl flex items-center justify-center"
                  >
                    ×
                  </button>
                </div>
                <p className="mt-2 font-body text-xs font-bold text-warmblack/50 tracking-wider">
                  ROOM CODE: <span className="font-display text-coral text-base tracking-[0.2em] ml-1">{room.code}</span>
                </p>
              </div>

              <div className="flex-1 overflow-y-auto px-3 py-3">
                <SectionHeader open={section === 'profile'} onClick={() => toggle('profile')} title="Your name" />
                {section === 'profile' && <NameEditor currentName={myName} send={send} onChangeName={onChangeName} />}

                <SectionHeader open={false} onClick={() => setInstructionsOpen(true)} title="How to play" />

                <SectionHeader open={section === 'leaderboard'} onClick={() => toggle('leaderboard')} title="Leaderboard" />
                {section === 'leaderboard' && <Leaderboard room={room} />}

                <SectionHeader open={section === 'admin'} onClick={() => toggle('admin')} title="Room admin" />
                {section === 'admin' && (
                  <AdminPanel
                    room={room}
                    playerId={playerId}
                    send={send}
                    isHost={isHost}
                    hostName={hostName}
                    myRequestPending={myRequestPending}
                    someoneElseRequested={someoneElseRequested}
                    requesterName={requesterName}
                    onRequestHost={requestHost}
                    onCancelRequest={cancelRequest}
                  />
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {instructionsOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-warmblack/40 backdrop-blur-sm flex items-end sm:items-center justify-center px-3 pb-3 pt-12"
            onClick={() => setInstructionsOpen(false)}
          >
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 30, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-cream rounded-2xl shadow-2xl max-w-md w-full max-h-full flex flex-col"
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-warmblack/10">
                <h2 className="font-display text-2xl font-bold text-warmblack">How to play</h2>
                <button
                  aria-label="Close"
                  onClick={() => setInstructionsOpen(false)}
                  className="w-8 h-8 rounded-lg hover:bg-warmblack/5 text-warmblack/60 text-2xl flex items-center justify-center"
                >
                  ×
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-5 py-4">
                <Instructions />
              </div>
              <div className="px-5 py-3 border-t border-warmblack/10">
                <button
                  onClick={() => setInstructionsOpen(false)}
                  className="w-full bg-coral hover:bg-coral/90 text-white font-display font-bold py-3 rounded-xl transition-colors"
                >
                  Got it
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

function SectionHeader({ open, onClick, title }: { open: boolean; onClick: () => void; title: string }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between px-3 py-3 rounded-xl hover:bg-warmblack/5 transition-colors text-left"
    >
      <span className="font-display text-lg font-bold text-warmblack">{title}</span>
      <motion.span
        animate={{ rotate: open ? 90 : 0 }}
        transition={{ duration: 0.15 }}
        className="text-warmblack/50 text-xl"
      >
        ›
      </motion.span>
    </button>
  )
}

function Leaderboard({ room }: { room: Room }) {
  const entries = Object.entries(room.leaderboard)
    .map(([id, score]) => {
      const p = room.players[id]
      return {
        id,
        score,
        name: p?.name || 'Left the room',
        stillHere: !!p && !p.departed,
      }
    })
    .sort((a, b) => b.score - a.score)

  if (entries.length === 0) {
    return (
      <p className="px-3 py-4 font-body text-sm text-warmblack/50 italic">
        No scores yet — finish a round to see the leaderboard.
      </p>
    )
  }

  return (
    <div className="px-3 py-2 space-y-1.5">
      {entries.map((e, i) => (
        <div
          key={e.id}
          className={`flex items-center justify-between px-4 py-2.5 rounded-xl ${
            i === 0 ? 'bg-sunflower/30' : 'bg-white'
          } ${!e.stillHere ? 'opacity-50' : ''}`}
        >
          <div className="flex items-center gap-3">
            <span className="font-display font-bold text-warmblack/50 w-6 text-center">
              {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}`}
            </span>
            <span className="font-body text-warmblack font-bold">{e.name}</span>
          </div>
          <span className="font-display text-xl font-bold text-warmblack">{e.score}</span>
        </div>
      ))}
    </div>
  )
}

function NameEditor({
  currentName,
  send,
  onChangeName,
}: {
  currentName: string
  send: SendFn
  onChangeName: (name: string) => void
}) {
  const [draft, setDraft] = useState(currentName)
  const [savedAt, setSavedAt] = useState<number | null>(null)
  const trimmed = draft.trim()
  const dirty = trimmed.length > 0 && trimmed !== currentName

  const save = () => {
    if (!dirty) return
    send('change_name', { name: trimmed })
    onChangeName(trimmed)
    setSavedAt(Date.now())
  }

  return (
    <div className="px-3 py-3 space-y-2">
      <div className="flex gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && save()}
          maxLength={30}
          className="flex-1 font-body text-base py-2.5 px-4 rounded-xl border-2 border-warmblack/10 bg-white focus:border-coral focus:outline-none transition-colors"
        />
        <button
          onClick={save}
          disabled={!dirty}
          className="bg-coral hover:bg-coral/90 disabled:opacity-40 disabled:hover:bg-coral text-white font-display font-bold px-5 rounded-xl shadow-sm transition-colors"
        >
          Save
        </button>
      </div>
      {savedAt && !dirty && (
        <p className="font-body text-xs text-sage">Saved.</p>
      )}
    </div>
  )
}

interface AdminPanelProps {
  room: Room
  playerId: string
  send: SendFn
  isHost: boolean
  hostName: string
  myRequestPending: boolean
  someoneElseRequested: boolean
  requesterName: string
  onRequestHost: () => void
  onCancelRequest: () => void
}

interface PendingConfirm {
  title: string
  message: string
  confirmLabel: string
  onConfirm: () => void
}

function AdminPanel({
  room,
  playerId,
  send,
  isHost,
  hostName,
  myRequestPending,
  someoneElseRequested,
  requesterName,
  onRequestHost,
  onCancelRequest,
}: AdminPanelProps) {
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null)
  const [pickHostOpen, setPickHostOpen] = useState(false)
  const players = Object.values(room.players)
  const connected = players.filter((p) => p.connected)
  // Dropped: still in the reconnection window (haven't given up).
  // Departed: left the room or window expired — seat is empty but claimable.
  const dropped = players.filter((p) => !p.connected && !p.departed)
  const departed = players.filter((p) => p.departed)

  const modeChangeable = room.phase === 'lobby' || room.phase === 'results'
  const activeRound = room.phase === 'discussion'
    || room.phase === 'rankerRanking'
    || room.phase === 'groupGuess'
    || room.phase === 'individualGuess'
    || room.phase === 'revealing'

  const setMode = (mode: 'individual' | 'group') => {
    if (!isHost || !modeChangeable || room.gameMode === mode) return
    send('set_game_mode', { mode })
  }

  const askConfirm = (pending: PendingConfirm) => () => setPendingConfirm(pending)

  const kick = (pid: string) => send('kick_player', { playerId: pid })
  const endGame = () => send('end_game', {})
  const skipRanker = () => send('skip_ranker', {})
  const resetLeaderboard = () => send('reset_leaderboard', {})

  return (
    <div className="px-3 py-3 space-y-4">
      <p className="font-body text-sm text-warmblack/60">
        Current host: <span className="font-bold text-warmblack">{hostName}</span>
      </p>

      {/* Host transfer */}
      <div className="space-y-2">
        {isHost && (
          <button
            onClick={() => setPickHostOpen(true)}
            className="w-full bg-coral hover:bg-coral/90 text-white font-display font-bold py-3 rounded-xl shadow-sm transition-colors"
          >
            Pick new host
          </button>
        )}
        {isHost && someoneElseRequested && (
          <p className="font-body text-sm text-warmblack/60 italic">
            {requesterName} has requested to be host — see the banner up top.
          </p>
        )}
        {!isHost && !myRequestPending && (
          <button
            onClick={onRequestHost}
            disabled={someoneElseRequested}
            className="w-full bg-coral hover:bg-coral/90 disabled:opacity-40 disabled:hover:bg-coral text-white font-display font-bold py-3 rounded-xl shadow-sm transition-colors"
          >
            Request to be host
          </button>
        )}
        {!isHost && someoneElseRequested && (
          <p className="font-body text-xs text-warmblack/50">
            {requesterName} already has a pending request — try again after it clears.
          </p>
        )}
        {!isHost && myRequestPending && (
          <>
            <p className="font-body text-sm text-warmblack/70">
              Request pending. Waiting for {hostName} to approve.
            </p>
            <button
              onClick={onCancelRequest}
              className="w-full bg-warmblack/10 hover:bg-warmblack/15 text-warmblack font-display font-bold py-3 rounded-xl transition-colors"
            >
              Cancel request
            </button>
          </>
        )}
      </div>

      {/* Game mode — hidden when only one mode exists. */}
      {GROUP_MODE_ENABLED && (
        <div className="space-y-1.5">
          <p className="font-body text-sm text-warmblack/60 font-bold">Game mode</p>
          {isHost ? (
            <>
              <div className="inline-flex bg-white rounded-full p-1 border border-warmblack/10 shadow-sm">
                <button
                  onClick={() => setMode('individual')}
                  disabled={!modeChangeable}
                  className={`font-body text-sm font-bold px-4 py-1.5 rounded-full transition-colors ${
                    room.gameMode === 'individual' ? 'bg-coral text-white' : 'text-warmblack/60'
                  } disabled:opacity-50`}
                >
                  Everyone votes
                </button>
                <button
                  onClick={() => setMode('group')}
                  disabled={!modeChangeable}
                  className={`font-body text-sm font-bold px-4 py-1.5 rounded-full transition-colors ${
                    room.gameMode === 'group' ? 'bg-coral text-white' : 'text-warmblack/60'
                  } disabled:opacity-50`}
                >
                  Group consensus
                </button>
              </div>
              {!modeChangeable && (
                <p className="font-body text-xs text-warmblack/50">
                  Can only change between rounds (lobby or results).
                </p>
              )}
            </>
          ) : (
            <p className="font-body text-sm text-warmblack/70">
              {room.gameMode === 'group' ? 'Group consensus' : 'Everyone votes'}
            </p>
          )}
        </div>
      )}

      {/* Host actions */}
      {isHost && (
        <div className="space-y-1.5">
          <p className="font-body text-sm text-warmblack/60 font-bold">Host actions</p>
          {activeRound && (
            <RankerAssigner
              connectedPlayers={connected.filter((p) => p.id !== room.currentRankerId)}
              currentRankerName={(room.currentRankerId && room.players[room.currentRankerId]?.name) || '—'}
              send={send}
              requestConfirm={setPendingConfirm}
            />
          )}
          {activeRound && (
            <button
              onClick={askConfirm({
                title: 'Skip current ranker?',
                message: 'The round restarts from discussion with a new ranker.',
                confirmLabel: 'Skip',
                onConfirm: skipRanker,
              })}
              className="w-full bg-white hover:bg-warmblack/5 text-warmblack font-display font-bold py-2.5 rounded-xl border border-warmblack/10 shadow-sm transition-colors"
            >
              Skip current ranker
            </button>
          )}
          {room.phase !== 'lobby' && (
            <button
              onClick={askConfirm({
                title: 'End this game?',
                message: 'Everyone returns to the lobby. The leaderboard is kept.',
                confirmLabel: 'End game',
                onConfirm: endGame,
              })}
              className="w-full bg-white hover:bg-warmblack/5 text-warmblack font-display font-bold py-2.5 rounded-xl border border-warmblack/10 shadow-sm transition-colors"
            >
              End game now
            </button>
          )}
          <button
            onClick={askConfirm({
              title: 'Reset leaderboard?',
              message: "This sets everyone's total back to zero.",
              confirmLabel: 'Reset',
              onConfirm: resetLeaderboard,
            })}
            className="w-full bg-white hover:bg-warmblack/5 text-warmblack font-display font-bold py-2.5 rounded-xl border border-warmblack/10 shadow-sm transition-colors"
          >
            Reset leaderboard
          </button>
        </div>
      )}

      {/* Player list */}
      <div className="space-y-1.5">
        <p className="font-body text-sm text-warmblack/60 font-bold">Players ({connected.length})</p>
        {connected.map((p) => (
          <div key={p.id} className="flex items-center justify-between bg-white border border-warmblack/10 rounded-xl px-3 py-2">
            <span className="font-body text-sm">
              <span className="font-bold text-warmblack">{p.name}</span>
              {p.id === room.hostId && <span className="text-warmblack/40"> (host)</span>}
              {p.id === playerId && <span className="text-warmblack/40"> (you)</span>}
            </span>
            {isHost && p.id !== room.hostId && p.id !== playerId && (
              <button
                onClick={askConfirm({
                  title: `Kick ${p.name}?`,
                  message: 'They will be removed from the room.',
                  confirmLabel: 'Kick',
                  onConfirm: () => kick(p.id),
                })}
                className="font-body text-xs text-coral hover:underline"
              >
                Kick
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Dropped players (still inside the reconnection window) */}
      {dropped.length > 0 && (
        <div className="space-y-1.5">
          <p className="font-body text-sm text-warmblack/60 font-bold">Dropped ({dropped.length})</p>
          <p className="font-body text-xs text-warmblack/50">
            Players whose connection dropped. {isHost ? 'You can reassign their seat to someone connected so their score and roles transfer.' : 'They have up to 2 minutes to reconnect.'}
          </p>
          {dropped.map((p) => (
            <DroppedRow
              key={p.id}
              droppedPlayer={p}
              connectedPlayers={connected}
              isHost={isHost}
              send={send}
              requestConfirm={setPendingConfirm}
            />
          ))}
        </div>
      )}

      {/* Departed: left the room (or window expired). Their name is still
          reserved so anyone joining with the same name gets the takeover
          prompt. Host can also reassign the seat directly. */}
      {departed.length > 0 && (
        <div className="space-y-1.5">
          <p className="font-body text-sm text-warmblack/60 font-bold">Left the room ({departed.length})</p>
          <p className="font-body text-xs text-warmblack/50">
            {isHost ? 'You can reassign their seat — score and history transfer to whoever picks it up.' : 'They left the room. They can rejoin with the same name to reclaim their seat.'}
          </p>
          {departed.map((p) => (
            <DroppedRow
              key={p.id}
              droppedPlayer={p}
              connectedPlayers={connected}
              isHost={isHost}
              send={send}
              requestConfirm={setPendingConfirm}
            />
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!pendingConfirm}
        title={pendingConfirm?.title}
        message={pendingConfirm?.message ?? ''}
        confirmLabel={pendingConfirm?.confirmLabel ?? 'Confirm'}
        onConfirm={() => {
          const action = pendingConfirm
          setPendingConfirm(null)
          action?.onConfirm()
        }}
        onCancel={() => setPendingConfirm(null)}
      />

      <PickHostDialog
        open={pickHostOpen}
        room={room}
        send={send}
        onClose={() => setPickHostOpen(false)}
      />
    </div>
  )
}

function PickHostDialog({
  open,
  room,
  send,
  onClose,
}: {
  open: boolean
  room: Room
  send: SendFn
  onClose: () => void
}) {
  // Only connected non-hosts can usefully take the role.
  const candidates = Object.values(room.players)
    .filter((p) => p.id !== room.hostId && p.connected)
    .sort((a, b) => a.name.localeCompare(b.name))

  const pick = (targetUserId?: string) => {
    send('pick_host', targetUserId ? { targetUserId } : {})
    onClose()
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-warmblack/40 z-[60]"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.92 }}
            transition={{ type: 'spring', damping: 22, stiffness: 320 }}
            className="fixed inset-0 z-[61] flex items-center justify-center px-6 pointer-events-none"
          >
            <div className="bg-cream rounded-2xl shadow-2xl px-6 py-5 max-w-sm w-full pointer-events-auto max-h-[80vh] flex flex-col">
              <h3 className="font-display text-xl font-bold text-warmblack mb-1">Pick new host</h3>
              <p className="font-body text-sm text-warmblack/70 mb-4">
                Choose a player, or hand it off at random.
              </p>
              <div className="flex-1 overflow-y-auto space-y-1.5 mb-3">
                {candidates.length === 0 ? (
                  <p className="font-body text-sm text-warmblack/50 italic">
                    No other connected players to choose from.
                  </p>
                ) : (
                  candidates.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => pick(p.id)}
                      className="w-full text-left bg-white hover:bg-cream border border-warmblack/10 rounded-xl px-4 py-2.5 font-body font-bold text-warmblack transition-colors"
                    >
                      {p.name}
                    </button>
                  ))
                )}
              </div>
              <button
                onClick={() => pick()}
                disabled={candidates.length === 0}
                className="w-full bg-sunflower hover:bg-sunflower/90 disabled:opacity-40 text-warmblack font-display font-bold py-2.5 rounded-xl shadow-sm transition-colors mb-2"
              >
                Pick at random
              </button>
              <button
                onClick={onClose}
                className="w-full bg-warmblack/10 hover:bg-warmblack/15 text-warmblack font-display font-bold py-2 rounded-xl transition-colors"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

function DroppedRow({
  droppedPlayer,
  connectedPlayers,
  isHost,
  send,
  requestConfirm,
}: {
  droppedPlayer: Room['players'][string]
  connectedPlayers: Room['players'][string][]
  isHost: boolean
  send: SendFn
  requestConfirm: (pending: PendingConfirm) => void
}) {
  const [target, setTarget] = useState<string>('')

  const reassign = () => {
    if (!target) return
    const newOwnerName = connectedPlayers.find((p) => p.id === target)?.name ?? 'them'
    const droppedUserId = droppedPlayer.id
    const newOwnerUserId = target
    requestConfirm({
      title: `Reassign ${droppedPlayer.name}'s seat?`,
      message: `${newOwnerName} will inherit their score and any active role.`,
      confirmLabel: 'Reassign',
      onConfirm: () => {
        send('reassign_seat', { droppedUserId, newOwnerUserId })
        setTarget('')
      },
    })
  }

  return (
    <div className="bg-white border border-warmblack/10 rounded-xl px-3 py-2 space-y-2">
      <p className="font-body text-sm font-bold text-warmblack/70">{droppedPlayer.name}</p>
      {isHost && connectedPlayers.length > 0 && (
        <div className="flex gap-2">
          <select
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            className="flex-1 font-body text-sm py-1.5 px-2 rounded-lg border border-warmblack/15 bg-white"
          >
            <option value="">Reassign to…</option>
            {connectedPlayers.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <button
            onClick={reassign}
            disabled={!target}
            className="bg-coral hover:bg-coral/90 disabled:opacity-40 text-white font-display font-bold px-3 py-1.5 rounded-lg text-sm shadow-sm transition-colors"
          >
            Confirm
          </button>
        </div>
      )}
    </div>
  )
}

function RankerAssigner({
  connectedPlayers,
  currentRankerName,
  send,
  requestConfirm,
}: {
  connectedPlayers: Room['players'][string][]
  currentRankerName: string
  send: SendFn
  requestConfirm: (pending: PendingConfirm) => void
}) {
  const [target, setTarget] = useState<string>('')

  const assign = () => {
    if (!target) return
    const name = connectedPlayers.find((p) => p.id === target)?.name ?? 'them'
    const targetId = target
    requestConfirm({
      title: `Make ${name} the ranker?`,
      message: 'The round will reset to discussion with them as the new ranker.',
      confirmLabel: 'Assign',
      onConfirm: () => {
        send('assign_ranker', { playerId: targetId })
        setTarget('')
      },
    })
  }

  return (
    <div className="bg-white border border-warmblack/10 rounded-xl px-3 py-2.5 space-y-2">
      <p className="font-body text-xs text-warmblack/60">
        Current ranker: <span className="font-bold text-warmblack">{currentRankerName}</span>
      </p>
      <div className="flex gap-2">
        <select
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          disabled={connectedPlayers.length === 0}
          className="flex-1 font-body text-sm py-1.5 px-2 rounded-lg border border-warmblack/15 bg-white disabled:opacity-50"
        >
          <option value="">Assign ranker to…</option>
          {connectedPlayers.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <button
          onClick={assign}
          disabled={!target}
          className="bg-coral hover:bg-coral/90 disabled:opacity-40 text-white font-display font-bold px-3 py-1.5 rounded-lg text-sm shadow-sm transition-colors"
        >
          Assign
        </button>
      </div>
    </div>
  )
}
